import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  AlertCircle
} from 'lucide-react';
import { YMaps, Map, Placemark } from '@pbe/react-yandex-maps';
import { supabase } from '../lib/supabase';
import { getYandexMapsQuery } from '../lib/yandexMaps';
import type { Database } from '../lib/database.types';
import Footer from '../components/Footer';

type BusinessProfile = Database['public']['Tables']['business_profiles']['Row'];

const PublicShop = () => {
  const { id } = useParams<{ id: string }>();
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('Invalid shop ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get the business profile
        const { data: businessData, error: businessError } = await supabase
          .from('business_profiles')
          .select('*')
          .eq('id', id)
          .single();

        if (businessError) throw businessError;
        if (!businessData) throw new Error('Business not found');

        setBusinessProfile(businessData);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load shop information');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yuklanmoqda...</p>
        </div>
      </div>
    );
  }

  if (error || !businessProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Xatolik</h1>
          <p className="text-gray-600 mb-4">
            {error || 'Do\'kon topilmadi'}
          </p>
          <p className="text-sm text-gray-500">
            QR kod noto'g'ri yoki do'kon o'chirilgan bo'lishi mumkin
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                PMaster
              </h1>
              <p className="text-gray-600 text-sm">
                Professional ta'mirlash xizmatlari
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">QR kod orqali</div>
              <div className="font-mono text-lg font-bold text-blue-600">
                Kirish
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Business Information */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-8 flex items-center gap-3">
            <MapPin className="w-6 h-6" />
            Biznes ma'lumotlari
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {businessProfile.business_name || businessProfile.master_name}
              </h3>
              {businessProfile.location && (
                <div className="flex items-start gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-gray-500">Manzil</div>
                    <div className="text-gray-900">{businessProfile.location}</div>
                    {businessProfile.latitude && businessProfile.longitude && (
                      <div className="mt-1 text-xs text-gray-500">
                        Koordinatalar: {businessProfile.latitude.toFixed(6)}, {businessProfile.longitude.toFixed(6)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {businessProfile.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-500" />
                  <div>
                    <div className="text-sm font-medium text-gray-500">Telefon</div>
                    <div className="text-gray-900 font-medium">{businessProfile.phone}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Map Section */}
          <div className="mt-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Joylashuv xaritasi
            </h3>
            
            {businessProfile.latitude && businessProfile.longitude ? (
              <>
                <div className="bg-gray-50 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                  <YMaps query={getYandexMapsQuery()}>
                    <Map
                      state={{
                        center: [businessProfile.latitude, businessProfile.longitude],
                        zoom: 15,
                        controls: ['zoomControl', 'fullscreenControl', 'typeSelector']
                      }}
                      width="100%"
                      height="100%"
                    >
                      <Placemark
                        geometry={[businessProfile.latitude, businessProfile.longitude]}
                        options={{
                          preset: 'islands#redDotIcon',
                          balloonContent: businessProfile.business_name || businessProfile.master_name || 'Biznes joylashuvi'
                        }}
                      />
                    </Map>
                  </YMaps>
                </div>
                <p className="mt-2 text-sm text-gray-500 text-center">
                  Xaritada bizning joylashuvimizni ko'ring
                </p>
              </>
            ) : (
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">Joylashuv xaritasi mavjud emas</p>
                <p className="text-sm text-gray-500">
                  Biznes egasi joylashuv koordinatalarini kiritmagan
                </p>
              </div>
            )}
          </div>

          {/* Call to Action */}
          <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Ta'mirlash xizmatiga ehtiyojingiz bormi?
              </h3>
              <p className="text-blue-700 mb-4">
                Bizning mutaxassislarimiz sizga yordam berishga tayyor
              </p>
              {businessProfile.phone && (
                <a 
                  href={`tel:${businessProfile.phone}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  Qo'ng'iroq qiling
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
      </div>
      <Footer />
    </div>
  );
};

export default PublicShop;
